import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import type { HeaderFilter } from '../../../contexts/DynamicHeaderContext';
import { GradientCheckbox } from './GradientCheckbox';

interface SelectFilterProps {
  filter: HeaderFilter;
}

export function SelectFilter({ filter }: SelectFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasValue = !!filter.value && filter.value !== filter.defaultValue;
  const selectedLabel = filter.options?.find(o => o.value === filter.value)?.label;

  const filteredOptions = useMemo(() => {
    if (!filter.searchable || !searchQuery.trim()) return filter.options;
    const words = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return filter.options?.filter(o => {
      const label = o.label.toLowerCase();
      return words.every(w => label.includes(w));
    });
  }, [filter.options, filter.searchable, searchQuery]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = filter.searchable ? 256 : 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = rect.bottom + 4;
      if (spaceBelow < 240 && spaceAbove > spaceBelow) top = rect.top - 240 - 4;
      const spaceRight = window.innerWidth - rect.left;
      let left = rect.left;
      if (spaceRight < dropdownWidth + 16) left = rect.right - dropdownWidth;
      left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));
      setPosition({ top: Math.max(8, top), left });
    }
  };

  const handleToggle = () => {
    if (!isOpen) updatePosition();
    else setSearchQuery('');
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) setIsOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const gradientTextStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #E91E8C, #F97316)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-xs rounded-md transition-colors ${
          hasValue
            ? 'bg-[rgba(233,30,140,0.08)] hover:bg-[rgba(233,30,140,0.13)]'
            : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
        }`}
      >
        <span style={hasValue ? gradientTextStyle : {}}>
          {hasValue ? selectedLabel : filter.label}
        </span>
        <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${hasValue ? 'text-[#E91E8C]' : 'text-muted-foreground'}`} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`fixed ${filter.searchable ? 'w-64' : 'w-48'} max-h-72 rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-white/10 z-[20050] flex flex-col`}
          style={{ top: position.top, left: position.left }}
        >
          {filter.searchable && (
            <div className="px-2 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={filter.placeholder || 'Cerca...'}
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded bg-gray-50 dark:bg-gray-700 border-0 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto py-1">
            <div
              onClick={() => { filter.onChange(filter.defaultValue); setIsOpen(false); setSearchQuery(''); }}
              className="flex items-center gap-2.5 px-2.5 py-2 hover:bg-gray-50 dark:hover:bg-white/10 cursor-pointer"
            >
              <GradientCheckbox checked={!hasValue} />
              <span className="text-sm text-gray-500 dark:text-gray-400">Tutti</span>
            </div>
            {filteredOptions?.map(opt => (
              <div
                key={opt.value}
                onClick={() => { filter.onChange(opt.value); setIsOpen(false); setSearchQuery(''); }}
                className="flex items-center gap-2.5 px-2.5 py-2 hover:bg-gray-50 dark:hover:bg-white/10 cursor-pointer"
              >
                <GradientCheckbox checked={filter.value === opt.value} />
                {opt.color && (
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
                )}
                <span className="text-sm text-gray-700 dark:text-gray-200">{opt.label}</span>
              </div>
            ))}
            {/* "Nessun risultato" ha senso solo se qualcosa si stava cercando.
                Quando il filtro non ha proprio voci — es. le famiglie prodotto
                di un'organizzazione che non ne ha ancora create — quel messaggio
                sotto una casella di ricerca vuota fa credere a una ricerca
                fallita, o peggio a un guasto. E senza ricerca il menu restava
                del tutto vuoto, senza dire niente. */}
            {filteredOptions?.length === 0 && (
              <div className="px-2.5 py-3 text-xs text-gray-400 text-center">
                {filter.options?.length ? 'Nessun risultato' : 'Nessuna voce configurata'}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
